import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  ReceiptRefundIcon,
  ArrowUpTrayIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { ProfessionalExpense, ExpenseKind } from '../types';
import { AddExpenseModal } from './AddExpenseModal';
import { ImportExpensesDropzone } from './ImportExpensesDropzone';

const KIND_LABELS: Record<ExpenseKind, string> = {
  suplido: 'Suplido',
  no_suplido: 'No suplido',
  profesional_deducible: 'Prof. deducible'
};

const KIND_OPTIONS: { value: ExpenseKind; title: string }[] = [
  { value: 'profesional_deducible', title: 'Gasto profesional deducible' },
  { value: 'no_suplido', title: 'No suplido (repercutible)' },
  { value: 'suplido', title: 'Suplido' }
];

type ViewTab = 'list' | 'import';

const KIND_BADGE: Record<ExpenseKind, string> = {
  suplido: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:ring-violet-800',
  no_suplido: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-800',
  profesional_deducible: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800'
};

export const ProfessionalExpensesManager: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [expenses, setExpenses] = useState<ProfessionalExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [viewTab, setViewTab] = useState<ViewTab>('list');
  const [importKind, setImportKind] = useState<ExpenseKind>('no_suplido');

  const load = useCallback(async () => {
    setLoading(true);
    const [listRes, sumRes] = await Promise.all([
      apiService.getProfessionalExpenses({ ejercicio: year, trimestre: quarter, includeLinked: true }),
      apiService.getExpenseQuarterlySummary(year, quarter)
    ]);
    const listData = listRes.data as { expenses?: ProfessionalExpense[] } | undefined;
    const sumData = sumRes.data as { summary?: Record<string, number> } | undefined;
    setExpenses(listData?.expenses || []);
    setSummary(sumData?.summary || {});
    setLoading(false);
  }, [year, quarter]);

  useEffect(() => {
    load();
  }, [load]);

  const pendientesIva = Number(summary.pendientesIvaExport || 0);

  const totals = useMemo(() => {
    const deducible = expenses
      .filter((e) => e.expenseKind === 'profesional_deducible')
      .reduce((s, e) => s + e.baseImponible, 0);
    const repercutibles = expenses
      .filter((e) => e.expenseKind === 'no_suplido')
      .reduce((s, e) => s + e.total, 0);
    const suplidos = expenses
      .filter((e) => e.expenseKind === 'suplido')
      .reduce((s, e) => s + e.total, 0);
    const iva = expenses.reduce((s, e) => s + e.ivaSoportado, 0);
    return { deducible, repercutibles, suplidos, iva, count: expenses.length };
  }, [expenses]);

  const fmt = (n: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

  const selectClass =
    'border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-[#161b22] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:border-[#635BFF] transition-colors';

  return (
    <div className="relative pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#0A2540] dark:text-[#e6edf3]">Gastos profesionales</h2>
          <p className="text-sm text-[#8898aa]">Suplidos, no suplidos (repercutibles) y gastos deducibles</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={selectClass}>
            {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className={selectClass}>
            {[1, 2, 3, 4].map((q) => (
              <option key={q} value={q}>T{q}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Gastos" value={String(totals.count)} />
        <StatCard label="Deducible IRPF" value={fmt(totals.deducible)} />
        <StatCard label="No suplido" value={fmt(totals.repercutibles)} />
        <StatCard label="Suplidos" value={fmt(totals.suplidos)} />
        <StatCard label="Pend. envío IVA" value={String(pendientesIva)} highlight={pendientesIva > 0} />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        <button
          type="button"
          onClick={() => setViewTab('list')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            viewTab === 'list'
              ? 'bg-[#635BFF] text-white shadow-sm'
              : 'bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] hover:border-[#635BFF]/40 hover:text-[#635BFF]'
          }`}
        >
          <ListBulletIcon className="h-3.5 w-3.5" />
          Lista
        </button>
        <button
          type="button"
          onClick={() => setViewTab('import')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            viewTab === 'import'
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] hover:border-emerald-400 hover:text-emerald-700'
          }`}
        >
          <ArrowUpTrayIcon className="h-3.5 w-3.5" />
          Importar en lote (IA)
        </button>
      </div>

      {viewTab === 'import' ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-[#F6F9FC] dark:bg-[#161b22] p-4">
            <p className="text-sm text-[#425466] dark:text-[#8b949e] mb-3 font-medium">
              Tipo de gasto para los tickets importados:
            </p>
            <div className="flex flex-wrap gap-2">
              {KIND_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setImportKind(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    importKind === opt.value
                      ? 'bg-[#635BFF] text-white shadow-sm'
                      : 'bg-white dark:bg-[#0d1117] border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] hover:border-[#635BFF]/40'
                  }`}
                >
                  {opt.title}
                </button>
              ))}
            </div>
          </div>
          <ImportExpensesDropzone
            expenseKind={importKind}
            onSaved={() => {
              load();
              setViewTab('list');
            }}
          />
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#635BFF]/30 border-t-[#635BFF] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-[#8898aa]">Cargando gastos…</p>
          </div>
        </div>
      ) : expenses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl border border-dashed border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22]">
          <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center mb-3">
            <ReceiptRefundIcon className="h-6 w-6 text-[#635BFF]" />
          </div>
          <p className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">No hay gastos en T{quarter} {year}</p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="mt-3 text-sm font-medium text-[#635BFF] hover:text-[#4F46E5] transition-colors"
          >
            Añadir el primero →
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#161b22] rounded-xl border border-[#E6E9F0] dark:border-[#21262d] shadow-stripe overflow-hidden overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[#E6E9F0] dark:border-[#21262d]">
                <th className="px-4 py-3 text-left text-xs font-medium text-[#8898aa] uppercase tracking-wider">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#8898aa] uppercase tracking-wider">Proveedor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#8898aa] uppercase tracking-wider">Tipo</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#8898aa] uppercase tracking-wider">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#8898aa] uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F6F9FC] dark:divide-[#21262d]">
              {expenses.map((exp) => (
                <tr key={exp.id} className="hover:bg-[#F6F9FC] dark:hover:bg-[#21262d]/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-[#425466] dark:text-[#8b949e]">{exp.fecha}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#0A2540] dark:text-[#e6edf3]">{exp.proveedor}</p>
                    {exp.descripcion && (
                      <p className="text-xs text-[#8898aa] truncate max-w-xs">{exp.descripcion}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${KIND_BADGE[exp.expenseKind]}`}>
                      {KIND_LABELS[exp.expenseKind]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-[#0A2540] dark:text-[#e6edf3] tabular-nums">{fmt(exp.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${exp.includedInVatExportAt ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800' : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-800'}`}>
                      {exp.includedInVatExportAt ? 'IVA enviado' : exp.estadoRevision}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewTab === 'list' && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="fixed bottom-8 right-8 flex items-center gap-2 px-4 py-2.5 bg-[#635BFF] text-white rounded-full shadow-stripe-md hover:bg-[#4F46E5] z-40 text-sm font-semibold transition-colors"
          aria-label="Añadir gasto"
        >
          <PlusIcon className="h-4 w-4" />
          Añadir gasto
        </button>
      )}

      {showAdd && (
        <AddExpenseModal
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label,
  value,
  highlight
}) => (
  <div
    className={`rounded-xl p-4 border ${
      highlight
        ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40'
        : 'bg-white dark:bg-[#161b22] border-[#E6E9F0] dark:border-[#21262d]'
    } shadow-stripe`}
  >
    <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider mb-1">{label}</p>
    <p className={`text-lg font-semibold tabular-nums ${highlight ? 'text-amber-700 dark:text-amber-400' : 'text-[#0A2540] dark:text-[#e6edf3]'}`}>{value}</p>
  </div>
);
